import * as React from 'react';
import * as R from 'ramda';
import Box from '@mui/material/Box';
import {RouteChildrenProps, useLocation} from 'react-router-dom';
import {styled} from '@mui/material/styles';
import {SocialAuthIntegration} from '@practicegenius/domain/src/data/integrations';
import {FieldInput} from '@practicegenius/pg-ui/src/base/Material/components/FieldInput';
import {PasswordInput} from '@practicegenius/pg-ui/src/base/Material/components/PasswordInput';
import {Checkbox} from '@practicegenius/pg-ui/src/base/Material/components/Checkbox';
import {TransitionGroup} from 'react-transition-group';
import {v4 as uuidv4} from 'uuid';
import {inMobileApp} from '@practicegenius/pg-ui/src/utils/MobileApp';
import {
  UnauthenticatedException,
  ValidationException,
} from '@practicegenius/http-clients-js/src/exceptions';
import {Field, Form} from '../../../@practicegenius/react-final-form';
import {EmailInput} from '../../../shared/Material/components/EmailFieldInput';
import {ErrorList} from '../../../@practicegenius/react-final-form/effects/handlesInitialErrors';
import {TextHr} from '../../../components/TextHr';
import {Link} from '../../../components/Link';
import {routes} from '../routes';
import {routes as registrationRoutes} from '../../register-account/routes';
import {UserLoginPayload} from '../../../types/loginTypes';
import {loginWithCredentials} from '../../../http/login';
import {withSessionErrors} from '../../../utils/php-js/withSessionErrors';
import {preverifyRewardsCard} from '../../../shared/http/register-account';
import {SociallyRegisteredModal} from './SociallyRegisteredModal';
import {queryStringToObject} from '../../../shared/utils/queryParams';
import {
  unregisteredAccountMethods,
  UnregisteredAccountModal,
} from './UnregisteredAccountModal';
import {OrphanedAccountModal} from './OrphanedAccountModal';
import {DeletedPracticeAccountModal} from './DeletedPracticeAccountModal';
import {UnregisteredSocialAccountModal} from './UnregisteredSocialAccountModal';
import {UserRateLimitedModal} from './UserRateLimitedModal';
import {GenericErrorModal} from './GenericErrorModal';
import log from '../../../utils/logger';
import {BaseScreen} from '../../../components/screens/BaseScreen';
import {ErrorMessage} from '../../../components/InlineError';
import {usePhpJs} from '../../../utils/php-js';
import {SocialLoginButtons} from '../../../components/SitesDotSocialLoginButtons';
import {LoginSupportFooter} from '../../../components/LoginSupportFooter';
import {RecaptchaFailedModal} from './RecaptchaFailedModal';
import {useRecaptcha} from '../../../shared/hooks/useRecaptcha';
import {VerifyHumanPanel} from '../components/VerifyHumanCheckbox';
import {useSyncSession} from '../../../shared/queries/global';

const RECAPTCHA_FAILURE_DISABLE_FORM_THRESHOLD = 3;
const RECAPTCHA_FAILURE_REQUIRE_VERIFICATION_THRESHOLD = 1;

// randomize the field names every time the module loads to add friction for any bots
const VERIFY_HUMAN_FIELD_NAME = uuidv4();
const HONEY_POT_FIELD_NAME = uuidv4();

const Modals = {
  UnregisteredCard: 'UnregisteredCard',
  UnregisteredTempRegCode: 'UnregisteredTempRegCode',
  UnregisteredSocialAccount: 'UnregisteredSocialAccount',
  RegistrationNotCompleted: 'RegistrationNotCompleted',
  OrphanedAccount: 'OrphanedAccount',
  DeletedPracticeAccount: 'DeletedPracticeAccount',
  SociallyRegistered: 'SociallyRegistered',
  UserThrottled: 'UserThrottled',
  RecaptchaFailed: 'RecaptchaFailed',
  GenericError: 'GenericError',
} as const;

type LoginState = {
  login: string;
  password: string;
  remember_me: boolean;
  humanVerification:
    | {
        required: false;
      }
    | {
        required: true;
        isHuman: boolean;
        isRobot: boolean;
      };
};

const stripLogin = (login: string): string => login.replace(/ /g, '');

const RememberMeWrapper = styled('div')({
  mt: '10px',
});

const ForgotLinkWrapper = styled('div')({
  display: 'flex',
  flexDirection: 'row-reverse',
});

const ForgotLink = styled(Link)({
  fontWeight: 'normal',
  width: 'max-content',
});

const InitialErrors = styled(ErrorMessage)({
  margin: 0,
});

class UnregisteredRewardsCardError extends Error {
  static ERROR_NAME = 'unregistered_card';
}

class UnregisteredTempRegCodeError extends Error {
  static ERROR_NAME = 'unregistered_username';
}

class UnregisteredSocialAccountError extends Error {
  static ERROR_NAME = 'unregistered_social_account';
}

class RegistrationNotFinishedError extends Error {
  static ERROR_NAME = 'registration_not_finished';
}

class OrphanedUserError extends Error {
  static ERROR_NAME = 'orphaned_user';
}

class SociallyRegisteredError extends Error {
  static ERROR_NAME = 'social_registered';
}

class UserThrottledError extends Error {
  static ERROR_NAME = 'user_throttled';
}

function Login({
  loginWithCredentials,
  initialErrors,
}: RouteChildrenProps & {
  initialErrors: ErrorList;
  loginWithCredentials: (
    login: string,
    password: string,
    csrf_token: string,
    recaptcha_token: string | null,
    remember_me: boolean,
    platform: string
  ) => Promise<UserLoginPayload>;
}) {
  // The registration app can redirect back to this screen with the identifier
  // of an already-registered user in the query string.
  const params = queryStringToObject();

  const location = useLocation<{
    showBackButton?: boolean;
  }>();
  const showBackButton = inMobileApp && location.state?.showBackButton;

  const csrfToken = usePhpJs(({csrf_token}) => csrf_token);

  const {mutateAsync: syncSession} = useSyncSession();
  const {getRecaptchaData, RecaptchaComponent} = useRecaptcha('LOGIN');
  const [recaptchaFailures, setRecaptchaFailures] = React.useState(0);

  const [modal, setModal] = React.useState<
    keyof typeof Modals | undefined | null
  >(null);
  const [socialAccountProps, setSocialAccountProps] = React.useState<{
    message: string;
    accounts: SocialAuthIntegration[];
  }>({
    message: '',
    accounts: [],
  });
  const [token, setToken] = React.useState<string | null>(null);
  const [unregisteredSocialProvider, setUnregisteredSocialProvider] =
    React.useState<SocialAuthIntegration | null>(null);
  const [userThrottlerProps, setUserThrottlerProps] = React.useState<{
    responseAction: any;
    availableIn: any;
    nextBlockDuration: any;
  } | null>(null);

  React.useEffect(() => {
    if (params.token) {
      setToken(params.token);
    }
  }, [params.token]);

  React.useEffect(() => {
    if (params.provider) {
      setUnregisteredSocialProvider(params.provider);
    }
  }, [params.provider]);

  React.useEffect(() => {
    if ('error' in params) {
      switch (params.error) {
        case UnregisteredSocialAccountError.ERROR_NAME:
          setModal(Modals.UnregisteredSocialAccount);
          break;
        case RegistrationNotFinishedError.ERROR_NAME:
          setModal(Modals.RegistrationNotCompleted);
          break;
        case OrphanedUserError.ERROR_NAME:
          setModal(
            params.was_deleted
              ? Modals.DeletedPracticeAccount
              : Modals.OrphanedAccount
          );
          break;
        default:
      }
    }
    // eslint-disable-next-line
  }, [params.error]);

  const handleLoginError = async (
    err: Error,
    opts: Partial<{
      login: string;
    }> = {}
  ) => {
    switch (true) {
      case err instanceof ValidationException:
        // Throw the validation error back up so our form can handle it
        throw err;
      case err instanceof UnauthenticatedException:
        // Just pass through to our other handlers below
        break;
      default:
        log.error('Sites. Login: Unhandled error response.', {
          message: err.message,
          // @ts-expect-error response isn't always on err, but the conditional access should help
          responseData: err.response?.data,
        });
        setModal(Modals.GenericError);
        return;
    }

    type UnauthenticatedErrorResponse = UnauthenticatedException['response'];
    interface ErrResponse extends UnauthenticatedErrorResponse {
      data: Record<string, unknown>;
    }
    interface Err extends UnauthenticatedException {
      response: ErrResponse;
    }
    const e = err as Err;

    /**
     * If we encounter an error from the server that the the user is trying to login with a rewards card,
     * and does not have a global user account, push them to the registration process.
     */
    if (
      e.response.data.error === UnregisteredRewardsCardError.ERROR_NAME &&
      opts.login
    ) {
      await preverifyRewardsCard(stripLogin(opts.login));
      setModal(Modals.UnregisteredCard);
      /**
       * This tree of logic is meant to be removed as cardless practices start using digital reward cards
       * @see https://practicegenius.atlassian.net/browse/PAT-8006
       */
    } else if (
      e.response.data.error === UnregisteredTempRegCodeError.ERROR_NAME &&
      opts.login
    ) {
      // This says it preverify's rewards cards...but it also works for usernames because of cardless offices (their generated username is their rewards card number).
      await preverifyRewardsCard(stripLogin(opts.login));
      setModal(Modals.UnregisteredTempRegCode);
    } else if (
      e.response.data.error === RegistrationNotFinishedError.ERROR_NAME
    ) {
      setToken(e.response.data.token as string);
      setModal(Modals.RegistrationNotCompleted);
    } else if (e.response.data.error === OrphanedUserError.ERROR_NAME) {
      setToken(e.response.data.token as string);
      setModal(
        e.response.data.was_deleted
          ? Modals.DeletedPracticeAccount
          : Modals.OrphanedAccount
      );
    } else if (e.response.data.error === SociallyRegisteredError.ERROR_NAME) {
      setSocialAccountProps({
        message: e.response.data.message as string,
        accounts: e.response.data.hint as SocialAuthIntegration[],
      });

      setModal(Modals.SociallyRegistered);
    } else if (e.response.data.error === UserThrottledError.ERROR_NAME) {
      setUserThrottlerProps({
        responseAction: e.response.data.response_action,
        availableIn: e.response.data.available_in,
        nextBlockDuration: e.response.data.next_block_duration,
      });

      setModal(Modals.UserThrottled);
    } else if (
      e.response.data.error === UnregisteredSocialAccountError.ERROR_NAME
    ) {
      setUnregisteredSocialProvider(
        e.response.data.provider as SocialAuthIntegration
      );
      setModal(Modals.UnregisteredSocialAccount);
    } else if (e.response.data.error === 'failed_recaptcha') {
      setRecaptchaFailures(R.inc);
      if (recaptchaFailures >= RECAPTCHA_FAILURE_DISABLE_FORM_THRESHOLD) {
        setModal(Modals.RecaptchaFailed);
      }
    } else {
      log.error('Sites. Login: Unhandled 401/403 response.', e);
      setModal(Modals.GenericError);
    }
  };

  const handleLogin = React.useCallback(
    async ({login, password, remember_me, ...state}: LoginState) => {
      try {
        const {recaptchaToken, platform} = await getRecaptchaData();

        if (state.humanVerification.required) {
          if (state.humanVerification.isRobot)
            throw new Error('User failed human verification');

          // this sets up the next login request to bypass the recaptcha check
          if (state.humanVerification.isHuman) await syncSession();
        }

        const payload = await loginWithCredentials(
          login,
          password,
          csrfToken,
          recaptchaToken,
          remember_me,
          platform
        );

        window.location.href = payload.redirect;
      } catch (err) {
        await handleLoginError(err as Error, {login});
      }
    },
    [loginWithCredentials]
  );

  const tooManyRecaptchaFailures =
    recaptchaFailures >= RECAPTCHA_FAILURE_DISABLE_FORM_THRESHOLD;
  const requiresHumanVerification =
    recaptchaFailures >= RECAPTCHA_FAILURE_REQUIRE_VERIFICATION_THRESHOLD;

  const initialValues = {
    login: params.user ?? '',
    password: '',
    remember_me: true,
    [VERIFY_HUMAN_FIELD_NAME]: false,
    [HONEY_POT_FIELD_NAME]: false,
  };

  return (
    <Form
      initialValues={initialValues}
      initialErrors={initialErrors}
      onSubmit={formData => {
        const data = formData as typeof initialValues;
        const humanVerification: LoginState['humanVerification'] =
          requiresHumanVerification
            ? {
                required: true,
                isHuman: data[VERIFY_HUMAN_FIELD_NAME],
                isRobot: data[HONEY_POT_FIELD_NAME],
              }
            : {
                required: false,
              };
        return handleLogin({...data, humanVerification} as LoginState);
      }}
    >
      {formRP => {
        const {pristine, initialErrors, values} = formRP;
        const disableSubmit =
          tooManyRecaptchaFailures ||
          (requiresHumanVerification && !values[VERIFY_HUMAN_FIELD_NAME]);

        return (
          <BaseScreen
            hideBackButton={!showBackButton}
            helmetTitle="Login"
            headerTitle="Log In"
            title="Log in with your membership account"
            actionButtons={[
              {
                // @ts-expect-error common data attribute
                'data-testid': 'submit',
                type: 'submit',
                children: 'Log In',
                loading: formRP.submitting,
                disabled: disableSubmit,
                color: 'primary' as const,
                customHeight: 'tall',
                customWidth: 'extraWide',
                bottomMargin: false,
              },
            ]}
            afterButtons={
              <>
                <Box my={1}>
                  <TextHr>or</TextHr>
                </Box>
                <SocialLoginButtons
                  platforms={['facebook', 'google', 'apple']}
                  /** Not point-free to satisfy TS on the second arg */
                  onLoginError={e => handleLoginError(e)}
                />
              </>
            }
            bottomText={
              <Box sx={{mt: 1, px: 2}}>
                <Box>
                  Don't have an account?{' '}
                  <Link
                    href={`/account/register${registrationRoutes.LinkPractice}`}
                  >
                    Register new account
                  </Link>
                </Box>
                <Box sx={{mt: 4}}>
                  This site is protected by reCAPTCHA and the Google{' '}
                  <Link
                    href="https://policies.google.com/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Privacy Policy
                  </Link>{' '}
                  and{' '}
                  <Link
                    href="https://policies.google.com/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Terms of Service
                  </Link>{' '}
                  apply.
                </Box>
                <LoginSupportFooter />
              </Box>
            }
          >
            <FieldInput
              name="login"
              label="Username or Email"
              component={EmailInput}
              autoComplete="username"
              autoFocus
            />
            <ForgotLinkWrapper>
              <ForgotLink
                to={{
                  pathname: routes.login.forgotUsername,
                  state: values,
                }}
              >
                Forgot Username
              </ForgotLink>
            </ForgotLinkWrapper>

            <FieldInput
              name="password"
              label="Password"
              component={PasswordInput}
              autoComplete="current-password"
            />
            <ForgotLinkWrapper>
              <ForgotLink
                to={{pathname: routes.login.forgotPassword, state: values}}
              >
                Forgot Password
              </ForgotLink>
            </ForgotLinkWrapper>

            {/** This is not shown in the mobile app. It will always be true per the form's initial values
             * @see https://practicegenius.atlassian.net/browse/PAT-13186
             */}
            {!inMobileApp && (
              <RememberMeWrapper>
                <Field name="remember_me" type="checkbox">
                  {({input}) => (
                    // @ts-ignore
                    <Checkbox
                      {...input}
                      checked={input.value}
                      label="Remember Me"
                    />
                  )}
                </Field>
              </RememberMeWrapper>
            )}

            {requiresHumanVerification && (
              <TransitionGroup>
                <VerifyHumanPanel
                  verifyHumanFieldName={VERIFY_HUMAN_FIELD_NAME}
                  honeyPotFieldName={HONEY_POT_FIELD_NAME}
                />
              </TransitionGroup>
            )}

            <OrphanedAccountModal
              isOpen={modal === Modals.OrphanedAccount}
              close={() => setModal(null)}
              redirectTo={`/account/register/link-a-practice?orphanedAccount=1&token=${String(
                token
              )}`}
            />

            <DeletedPracticeAccountModal
              isOpen={modal === Modals.DeletedPracticeAccount}
              close={() => setModal(null)}
            />

            <UnregisteredAccountModal
              isOpen={[
                Modals.UnregisteredCard,
                Modals.RegistrationNotCompleted,
                // @ts-ignore
              ].includes(modal)}
              close={() => setModal(null)}
              methodUsed={unregisteredAccountMethods.REWARDS_NUMBER}
              redirectTo={loginValue => {
                if (modal === Modals.UnregisteredCard) {
                  return `/account/register/registration-methods?unregisteredInput=${loginValue}`;
                }
                return `/account/register/link-a-practice?token=${String(
                  token
                )}&linkingViaSocial=${Number(params.viaSocial) || 0}`;
              }}
            />

            <UnregisteredAccountModal
              isOpen={modal === Modals.UnregisteredTempRegCode}
              close={() => setModal(null)}
              methodUsed={unregisteredAccountMethods.TEMP_REG_CODE}
              redirectTo={loginValue =>
                `/account/register/registration-methods?unregisteredInput=${loginValue}`
              }
            />

            <UnregisteredSocialAccountModal
              isOpen={modal === Modals.UnregisteredSocialAccount}
              close={() => setModal(null)}
              provider={unregisteredSocialProvider ?? 'facebook'}
            />

            <SociallyRegisteredModal
              isOpen={modal === Modals.SociallyRegistered}
              close={() => setModal(null)}
              message={socialAccountProps.message}
              socialAccounts={socialAccountProps.accounts}
              /** Not point-free to satisfy TS on the second arg */
              onLoginError={e => handleLoginError(e)}
            />

            {userThrottlerProps && (
              <UserRateLimitedModal
                isOpen={modal === Modals.UserThrottled}
                close={() => setModal(null)}
                formValues={values}
                responseAction={userThrottlerProps.responseAction}
                availableIn={userThrottlerProps.availableIn}
                nextBlockDuration={userThrottlerProps.nextBlockDuration}
              />
            )}

            <RecaptchaFailedModal
              isOpen={modal === Modals.RecaptchaFailed}
              close={() => setModal(null)}
            />

            <GenericErrorModal
              open={modal === Modals.GenericError}
              close={() => setModal(null)}
            />
            {initialErrors && pristine && (
              <InitialErrors>{formRP.initialErrors}</InitialErrors>
            )}

            {RecaptchaComponent}
          </BaseScreen>
        );
      }}
    </Form>
  );
}

export default withSessionErrors(Login);

Login.defaultProps = {
  loginWithCredentials,
};
